/*
 * Moona Smart Header customizations
 * Version: 1.0.2
 */

(() => {
  'use strict';

  if (window.MoonaHeader?.version === '1.0.2') {
    window.MoonaHeader.refresh();
    return;
  }

  const MARKER_SELECTOR = 'moona-header-customizations[data-section-id]';
  const ROOT_CLASS = 'moona-header-scope';
  const MOBILE_REFRESH_DELAYS = [0, 80, 250, 650, 1400, 2600];
  const rootStates = new WeakMap();

  const getMarker = (root) => root?.querySelector(MARKER_SELECTOR);

  const getRoot = (target) => {
    if (!(target instanceof Element)) return null;

    return target.closest(`.${ROOT_CLASS}`);
  };

  const hoverIsEnabled = (root) => {
    return getMarker(root)?.dataset.hoverEnabled === 'true';
  };

  const setMenuState = (menu, open) => {
    if (!menu) return;

    /* Preserve the theme's own animation, overlay and accessibility logic. */
    menu.open = open;

    const summary = menu.querySelector(':scope > summary');

    if (summary) {
      summary.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  };

  const getTopMenu = (target) => {
    if (!(target instanceof Element)) return null;

    const menu = target.closest('details[level="top"][trigger="hover"]');
    const root = getRoot(target);

    return menu && root && root.contains(menu) ? menu : null;
  };

  const openMenu = (root, menu) => {
    root
      .querySelectorAll('details[level="top"][trigger="hover"]')
      .forEach((otherMenu) => {
        if (otherMenu !== menu && otherMenu.open) {
          setMenuState(otherMenu, false);
        }
      });

    if (!menu.open) setMenuState(menu, true);
  };

  const makeDesktopLabels = (root) => {
    root
      .querySelectorAll('.mega-menu__nav-item > a.heading')
      .forEach((link) => {
        const label = document.createElement('span');

        label.className = link.className;
        label.innerHTML = link.innerHTML;
        label.classList.remove('reversed-link');
        label.classList.add('moona-menu-label');
        label.setAttribute('role', 'heading');
        label.setAttribute('aria-level', '3');

        link.replaceWith(label);
      });
  };

  const buildMobileMenu = (root) => {
    root
      .querySelectorAll(
        'menu-drawer [id^="DrawerSubMenu-"] > ' +
          '.drawer__menu-group > details'
      )
      .forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        const submenu = details.querySelector(':scope > .drawer__submenu');
        const links = submenu?.querySelector(':scope > ul.drawer__scrollable');

        if (!summary || !links) return;

        const sectionItem = details.parentElement;
        const label = document.createElement('div');

        label.className = 'moona-mobile-menu-label';
        label.textContent = summary.textContent.trim();
        label.setAttribute('role', 'heading');
        label.setAttribute('aria-level', '3');

        links.classList.add('moona-mobile-menu-links');
        sectionItem.classList.add('moona-mobile-menu-section');

        /*
         * Keep the top-level Smart Homes tap behavior, but flatten the next
         * level into gray labels followed by directly visible page links.
         */
        sectionItem.replaceChildren(label, links);
      });
  };

  const applyEnhancements = (root) => {
    makeDesktopLabels(root);
    buildMobileMenu(root);
  };

  const initRoot = (root) => {
    if (!root || !getMarker(root)) return;

    root.classList.add(ROOT_CLASS);
    applyEnhancements(root);

    if (rootStates.has(root)) return;

    const state = {
      closeTimer: null,
      observer: new MutationObserver(() => applyEnhancements(root))
    };

    state.observer.observe(root, {
      childList: true,
      subtree: true
    });

    rootStates.set(root, state);
  };

  const refresh = (scope = document) => {
    scope.querySelectorAll(MARKER_SELECTOR).forEach((marker) => {
      initRoot(marker.closest('.shopify-section'));
    });
  };

  document.addEventListener('pointerover', (event) => {
    if (window.innerWidth < 1024 || event.pointerType === 'touch') return;

    const menu = getTopMenu(event.target);
    const root = getRoot(event.target);

    if (!menu || !root || !hoverIsEnabled(root)) return;

    if (event.relatedTarget && menu.contains(event.relatedTarget)) return;

    const state = rootStates.get(root);
    if (state?.closeTimer) clearTimeout(state.closeTimer);

    openMenu(root, menu);
  });

  document.addEventListener('pointerout', (event) => {
    if (window.innerWidth < 1024 || event.pointerType === 'touch') return;

    const menu = getTopMenu(event.target);
    const root = getRoot(event.target);

    if (!menu || !root || !hoverIsEnabled(root)) return;

    if (event.relatedTarget && menu.contains(event.relatedTarget)) return;

    const state = rootStates.get(root);
    if (!state) return;

    if (state.closeTimer) clearTimeout(state.closeTimer);

    state.closeTimer = setTimeout(() => {
      if (!menu.matches(':hover') && !menu.contains(document.activeElement)) {
        setMenuState(menu, false);
      }
    }, 180);
  });

  /*
   * Concept loads mobile submenu HTML through the Section Rendering API only
   * after a visitor taps a top-level item. Theme Editor renders that HTML up
   * front, so repeat the mobile conversion while the live response arrives.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (window.innerWidth >= 1024 || !(event.target instanceof Element)) {
        return;
      }

      const topLevelSummary = event.target.closest(
        'menu-drawer .drawer__menu > .drawer__menu-group > details > summary'
      );

      if (!topLevelSummary) return;

      MOBILE_REFRESH_DELAYS.forEach((delay) => {
        setTimeout(() => refresh(), delay);
      });
    },
    true
  );

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target;
    const state = rootStates.get(root);

    if (!state) return;

    if (state.closeTimer) clearTimeout(state.closeTimer);
    state.observer.disconnect();
    rootStates.delete(root);
  });

  window.MoonaHeader = {
    version: '1.0.2',
    refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), {
      once: true
    });
  } else {
    refresh();
  }
})();
